import React, { useEffect, useState } from "react";
import BaseModalAnimated from "../../components/modal/BaseModalAnimated";
import {
    getModules,
    getPermissions,
    rolsResource,
} from "../../helpers/configRoutes/backend_helper";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import CustomInput from "../../components/form/CustomInput";
import CustomButton from "../../components/form/CustomButton";
import { showFormErrors } from "../../helpers/showFormErrors";

const buildInitialModels = (rol) => {
    if (!rol) return [];
    // Si ya viene en el formato correcto
    if (Array.isArray(rol.models)) {
        return rol.models.map((m) => ({
            model_id: Number(m.model_id),
            permission_id: Number(m.permission_id),
        }));
    }
    // Si viene como models_permissions (clásico de pivot)
    if (Array.isArray(rol.models_permissions)) {
        return rol.models_permissions.map((mp) => ({
            model_id: Number(mp.model_id),
            permission_id: Number(mp.permission_id),
        }));
    }
    return [];
};

const AddUpdRol = ({ modal, setModal, setFilters, filters }) => {
    const [permissions, setPermissions] = useState([]);
    const [models, setModels] = useState([]);
    const { rol, rolValidations } = useSelector((state) => state.Rol);
    const [loading, setLoading] = useState(false);

    // Cargar catálogos cuando se abre el modal
    useEffect(() => {
        const getCatalogs = async () => {
            const responseModels = await getModules();
            const responsePermissions = await getPermissions();
            setModels(responseModels?.models || []);
            setPermissions(responsePermissions?.permissions || []);
        };

        if (modal.show) {
            getCatalogs();
        }
    }, [modal.show]);

    const validation = useFormik({
        initialValues: {
            id: rol?.id || null,
            name: rol?.name || "",
            description: rol?.description || "",
            models: buildInitialModels(rol),
        },
        enableReinitialize: true,
        validationSchema: Yup.object(rolValidations),
        onSubmit: async (values, { setErrors }) => {
            setLoading(true);

            const payload = {
                name: values.name,
                description: values.description,
                models: (values.models || []).map((item) => ({
                    model_id: Number(item.model_id),
                    permission_id: Number(item.permission_id),
                })),
            };

            const response = await rolsResource(
                values.id ? "put" : "post",
                payload,
                values.id
            );

            if (response.status) {
                setFilters({ ...filters, search: filters.search });
                setModal({ ...modal, show: false });
            } else {
                setErrors(response.errors || {});
            }

            setLoading(false);
        },
    });

    // Saber si un permiso está seleccionado para un módulo
    const isPermissionSelected = (modelId, permissionId) => {
        return (
            validation.values.models?.some(
                (m) =>
                    Number(m.model_id) === Number(modelId) &&
                    Number(m.permission_id) === Number(permissionId)
            ) || false
        );
    };

    // Saber si un módulo tiene al menos un permiso
    const moduleHasPermissions = (modelId) => {
        return (
            validation.values.models?.some(
                (m) => Number(m.model_id) === Number(modelId)
            ) || false
        );
    };

    // Toggle de permisos (agregar/quitar del array)
    const handleTogglePermission = (modelId, permissionId) => {
        validation.setFieldTouched("models", true, false);

        validation.setValues((prev) => {
            const current = prev.models || [];
            const existsIndex = current.findIndex(
                (m) =>
                    Number(m.model_id) === Number(modelId) &&
                    Number(m.permission_id) === Number(permissionId)
            );

            let updated;
            if (existsIndex !== -1) {
                // Si ya existe, lo quitamos
                updated = [...current];
                updated.splice(existsIndex, 1);
            } else {
                // Si no existe, lo agregamos
                updated = [
                    ...current,
                    { model_id: Number(modelId), permission_id: Number(permissionId) },
                ];
            }

            return {
                ...prev,
                models: updated,
            };
        });
    };

    // Contar módulos únicos seleccionados
    const uniqueModelsSelected = Array.from(
        new Set(
            (validation.values.models || []).map((m) =>
                Number(m.model_id)
            )
        )
    ).length;

    return (
        <BaseModalAnimated
            modal={modal}
            setModal={setModal}
            size={'max-w-4xl'}
            title={
                modal.title ||
                (validation.values.id ? "Edit role" : "Create role")
            }
        >
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                    showFormErrors(validation);
                    return false;
                }}
            >
                <div className="p-2 md:p-2 space-y-5">
                    {/* Intro */}
                    <div className="flex flex-col gap-1">
                        <p className="text-sm uppercase tracking-[0.16em] text-cs-primary-dark">
                            Role configuration
                        </p>
                        <p className="text-sm text-cs-muted">
                            Define the role details and select what each module can do.
                        </p>
                    </div>

                    <div className="grid grid-cols-2">
                        {/* Name */}
                        <CustomInput
                            label="Name"
                            type="text"
                            name="name"
                            placeholder="Enter role name"
                            labelClassName="text-[11px] text-cs-muted"
                            error={validation.errors?.name}
                            value={validation.values?.name || ""}
                            handleChange={validation.handleChange}
                        />

                        {/* Description */}
                        <CustomInput
                            label="Description"
                            type="text"
                            name="description"
                            placeholder="Short description for this role"
                            labelClassName="text-[11px] text-cs-muted"
                            error={validation.errors?.description}
                            value={validation.values?.description || ""}
                            handleChange={validation.handleChange}
                        />
                    </div>
                    
                    {/* Modules & Permissions */}
                    <div className="">
                        <div className="flex items-center justify-between mb-1">
                            <div>
                                <h3 className="text-[11px] font-semibold text-cs-primary-dark">
                                    Modules & permissions
                                </h3>
                                <p className="text-[9px] text-cs-muted">
                                    You can assign multiple permissions per module. Click to toggle.
                                </p>
                            </div>
                            <span className="px-2 py-0.5 rounded-full bg-cs-primary-soft text-cs-primary-dark text-[9px]">
                                {uniqueModelsSelected} modules with access
                            </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
                            {models.map((model) => {
                                const active = moduleHasPermissions(model.id);

                                return (
                                    <div
                                        key={model.id}
                                        className={`
                                            border rounded-2xl p-1 flex flex-col gap-2
                                            bg-cs-bg
                                            transition-all
                                            ${active
                                                ? "border-cs-primary-soft bg-cs-surface-alt shadow-sm"
                                                : "border-cs-border"
                                            }
                                        `}
                                    >
                                        {/* Header módulo */}
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex items-center gap-2">
                                                <div
                                                    className="
                                                        w-7 h-7 rounded-xl
                                                        bg-cs-primary-soft
                                                        flex items-center justify-center
                                                        text-sm font-semibold text-cs-primary-dark
                                                    "
                                                >
                                                    {model.name
                                                        .split(" ")
                                                        .map((w) => w[0])
                                                        .join("")
                                                        .slice(0, 3)
                                                        .toUpperCase()}
                                                </div>
                                                <div className="flex flex-col leading-tight">
                                                    <span className="text-sm font-semibold text-cs-dark">
                                                        {model.name}
                                                    </span>
                                                    <span className="text-[11px] text-cs-muted">
                                                        {model.link || model.system || "Module"}
                                                    </span>
                                                </div>
                                            </div>

                                            {active && (
                                                <span className="px-1.5 py-0.5 rounded-full bg-cs-primary text-white text-[11px] flex items-center gap-1">
                                                    <span className="material-symbols-outlined text-sm">
                                                        check_circle
                                                    </span>
                                                    Active
                                                </span>
                                            )}
                                        </div>

                                        {/* Permisos como pills togglables */}
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {permissions.map((perm) => {
                                                const selected = isPermissionSelected(
                                                    model.id,
                                                    perm.id
                                                );

                                                // Estilos según el tipo de permiso (solo para UX bonito)
                                                const lower = (perm.name || "").toLowerCase();
                                                let baseClasses =
                                                    "px-2 py-0.5 rounded-full text-[11px] border cursor-pointer transition-all inline-flex items-center gap-1";
                                                let inactiveClasses =
                                                    "bg-cs-bg-alt border-cs-border text-cs-muted hover:bg-cs-primary-soft hover:text-cs-primary";
                                                let activeClasses =
                                                    "bg-cs-primary-soft border-cs-primary text-cs-primary-dark";

                                                if (lower.includes("total")) {
                                                    activeClasses =
                                                        "bg-cs-primary text-white border-cs-primary";
                                                } else if (lower.includes("export")) {
                                                    activeClasses =
                                                        "bg-cs-surface-alt border-cs-primary-soft text-cs-primary-dark";
                                                }

                                                return (
                                                    <button
                                                        key={perm.id}
                                                        type="button"
                                                        onClick={() =>
                                                            handleTogglePermission(
                                                                model.id,
                                                                perm.id
                                                            )
                                                        }
                                                        className={`
                                                            ${baseClasses}
                                                            ${selected
                                                                ? activeClasses
                                                                : inactiveClasses
                                                            }
                                                        `}
                                                    >
                                                        {selected && (
                                                            <span className="material-symbols-outlined text-sm">
                                                                check
                                                            </span>
                                                        )}
                                                        <span>{perm.name}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {validation.errors?.models && (
                            <p className="mt-1 text-[9px] text-red-500">
                                {validation.errors.models}
                            </p>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-2 pt-3 border-t border-cs-border/60 mt-3">
                        <button
                            type="button"
                            onClick={() => setModal({ ...modal, show: false })}
                            className="
                                px-3 py-2 rounded-xl
                                text-sm text-cs-muted
                                bg-cs-bg
                                hover:bg-cs-bg-alt
                                border border-cs-border
                                transition-all
                            "
                        >
                            Cancel
                        </button>

                        <CustomButton
                            loading={loading}
                            textLoad="Saving..."
                            type="submit"
                            className="
                                bg-cs-primary
                                hover:bg-cs-primary-hover
                                text-white
                                px-4 py-2
                                rounded-xl
                                text-sm
                                font-medium
                                flex items-center gap-1
                            "
                        >
                            <span className="material-symbols-outlined text-[14px]">
                                save
                            </span>
                            Save role
                        </CustomButton>
                    </div>
                </div>
            </form>
        </BaseModalAnimated>
    );
};

export default AddUpdRol;
